import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, tokenStorage } from '@/lib/api';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  language: string;
  avatarUrl?: string;
}

interface RegisterResult {
  email: string;
  requiresVerification: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  register: (
    name: string,
    email: string,
    password: string,
    language?: string,
  ) => Promise<RegisterResult>;
  verifyOtp: (email: string, code: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const { data } = await api.post('/auth/login', { email, password });
          tokenStorage.setTokens(data.accessToken, data.refreshToken);
          set({ user: data.user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (name: string, email: string, password: string, language?: string) => {
        set({ isLoading: true });
        try {
          const { data } = await api.post('/auth/register', { name, email, password, language });
          set({ isLoading: false });
          // No tokens yet — the user must verify their email via OTP.
          return {
            email: data.email ?? email,
            requiresVerification: data.requiresVerification ?? true,
          };
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      verifyOtp: async (email: string, code: string) => {
        set({ isLoading: true });
        try {
          const { data } = await api.post('/auth/verify-otp', { email, code });
          tokenStorage.setTokens(data.accessToken, data.refreshToken);
          set({ user: data.user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      resetPassword: async (email: string, code: string, newPassword: string) => {
        set({ isLoading: true });
        try {
          const { data } = await api.post('/auth/reset-password', {
            email,
            code,
            newPassword,
          });
          // Backend returns fresh tokens so we can log the user straight in.
          tokenStorage.setTokens(data.accessToken, data.refreshToken);
          set({ user: data.user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          const refreshToken = tokenStorage.getRefreshToken();
          await api.post('/auth/logout', { refreshToken });
        } catch {
          // Ignore errors during logout
        }
        tokenStorage.clearTokens();
        set({ user: null, isAuthenticated: false });
      },

      refreshUser: async () => {
        try {
          const { data } = await api.get('/users/profile');
          set({ user: data, isAuthenticated: true });
        } catch {
          tokenStorage.clearTokens();
          set({ user: null, isAuthenticated: false });
        }
      },

      updateUser: (data: Partial<User>) => {
        const current = get().user;
        if (current) {
          set({ user: { ...current, ...data } });
        }
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
